import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Movie } from '@filmsloth/shared';
import { api, type WatchlistEntry, type WatchlistKind } from '../api/client.js';

interface WatchlistValue {
  entries: WatchlistEntry[];
  loading: boolean;
  /** Whether this film is already on the given list. */
  has: (movieId: string, kind: WatchlistKind) => boolean;
  toggle: (movie: Movie, kind: WatchlistKind) => Promise<void>;
  remember: (movie: Movie, kind: WatchlistKind) => Promise<void>;
  reload: () => Promise<void>;
}

const WatchlistContext = createContext<WatchlistValue | null>(null);

/**
 * The films this device kept.
 *
 * Loaded once and then kept in step optimistically: saving a film is a small,
 * reversible act, and waiting for a round trip before the star fills in makes
 * it feel broken.
 *
 * Failures are swallowed rather than shown. A watchlist that did not save is
 * a disappointment, not an error worth interrupting a film night for — the
 * next reload will simply show the truth.
 */
export function WatchlistProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<WatchlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const result = await api.watchlist();
      setEntries(result.entries);
    } catch {
      // Offline, or the cookie is new. An empty list is the honest default.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<WatchlistValue>(() => {
    const has = (movieId: string, kind: WatchlistKind) =>
      entries.some((entry) => entry.movieId === movieId && entry.kind === kind);

    const remember = async (movie: Movie, kind: WatchlistKind) => {
      if (has(movie.id, kind)) return;
      const optimistic: WatchlistEntry = {
        movieId: movie.id,
        title: movie.title,
        kind,
        addedAt: new Date().toISOString(),
      };
      setEntries((current) => [optimistic, ...current]);
      try {
        await api.addToWatchlist({ movieId: movie.id, title: movie.title, kind });
      } catch {
        setEntries((current) =>
          current.filter((entry) => !(entry.movieId === movie.id && entry.kind === kind)),
        );
      }
    };

    const forget = async (movieId: string, kind: WatchlistKind) => {
      const removed = entries.filter((entry) => entry.movieId === movieId && entry.kind === kind);
      setEntries((current) =>
        current.filter((entry) => !(entry.movieId === movieId && entry.kind === kind)),
      );
      try {
        await api.removeFromWatchlist(movieId, kind);
      } catch {
        setEntries((current) => [...removed, ...current]);
      }
    };

    return {
      entries,
      loading,
      has,
      remember,
      toggle: async (movie, kind) => {
        if (has(movie.id, kind)) await forget(movie.id, kind);
        else await remember(movie, kind);
      },
      reload,
    };
  }, [entries, loading, reload]);

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
}

export function useWatchlist(): WatchlistValue {
  const value = useContext(WatchlistContext);
  if (!value) throw new Error('useWatchlist must be used inside <WatchlistProvider>');
  return value;
}
